import { LegalLayout } from '@/components/layout';
import { AlertTriangle } from 'lucide-react';
import { SEO } from '@/components/SEO';

export function TermsOfServicePage() {
  return (
    <>
    <SEO
      title="Terms of Service - Vettis"
      description="Vettis Terms of Service. Understand your rights, responsibilities, and our AI research service policies."
      path="/terms"
    />
    <LegalLayout title="Terms of Service" lastUpdated="January 11, 2026">
      <section className="space-y-6">
        <div className="bg-surface rounded-lg p-6 border border-border">
          <p className="text-text-secondary">
            These Terms of Service ("Terms") govern your use of the Vettis AI-powered research service ("Service"). By using our Service, you agree to these Terms.
          </p>
        </div>

        <div className="space-y-8">
          {/* Acceptance */}
          <div>
            <h2 className="text-2xl font-semibold text-text-primary mb-4">1. Acceptance of Terms</h2>
            <div className="space-y-4 text-text-secondary">
              <p>By accessing or using Vettis, you agree to be bound by these Terms and our Privacy Policy. If you do not agree to these Terms, you may not use our Service.</p>
              <p>You must be at least 18 years old to use this Service. By using the Service, you represent that you meet this age requirement.</p>
            </div>
          </div>

          {/* Service Description */}
          <div>
            <h2 className="text-2xl font-semibold text-text-primary mb-4">2. Service Description</h2>
            <div className="space-y-4 text-text-secondary">
              <p>Vettis is an AI-powered research service that:</p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Generates comprehensive research content using multiple AI models</li>
                <li>Delivers research to your email at your specified time</li>
                <li>Allows customization of topics and research prompts</li>
                <li>Provides web research integration and fact-checking</li>
              </ul>
            </div>
          </div>

          {/* AI-Generated Content Disclaimer */}
          <div>
            <h2 className="text-2xl font-semibold text-text-primary mb-4">3. AI-Generated Content Disclaimer</h2>
            <div className="bg-warning/10 border border-warning/30 rounded-lg p-6 space-y-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-warning flex-shrink-0 mt-0.5" />
                <div className="space-y-4 text-text-secondary">
                  <p className="font-semibold text-warning">IMPORTANT: Please read carefully</p>
                  <p><strong>All content generated by Vettis is produced by artificial intelligence systems and should be treated as informational only.</strong></p>
                  <ul className="list-disc list-inside ml-4 space-y-2">
                    <li><strong>No Guarantee of Accuracy:</strong> AI-generated content may contain errors, inaccuracies, or outdated information. We do not guarantee the accuracy, completeness, or reliability of any AI-generated content.</li>
                    <li><strong>Not Professional Advice:</strong> Content should not be construed as professional, financial, legal, medical, or any other type of advice. Always consult qualified professionals before making important decisions.</li>
                    <li><strong>AI Limitations:</strong> AI models may produce hallucinations (fabricated information presented as fact), biased content, or inappropriate responses despite our best efforts to prevent this.</li>
                    <li><strong>No Investment Advice:</strong> Any financial or investment-related content is for informational purposes only and does not constitute investment advice. Past performance discussed does not guarantee future results.</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* User Responsibilities */}
          <div>
            <h2 className="text-2xl font-semibold text-text-primary mb-4">4. User Responsibilities</h2>
            <div className="space-y-4 text-text-secondary">
              <p>You agree to:</p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Provide accurate account information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Use the Service in compliance with all applicable laws</li>
                <li>Not use the Service for any illegal or unauthorized purpose</li>
                <li>Not attempt to circumvent any usage limits or security measures</li>
                <li>Verify important information from AI-generated content independently</li>
              </ul>

              <h3 className="text-lg font-medium text-text-primary mt-6">Prohibited Uses</h3>
              <p>You may not use our Service to:</p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Generate content that is illegal, harmful, or violates third-party rights</li>
                <li>Attempt to extract or reverse-engineer our AI systems</li>
                <li>Redistribute or resell AI-generated content commercially without permission</li>
                <li>Use the Service to spam or harass others</li>
                <li>Upload malicious files or content</li>
              </ul>
            </div>
          </div>

          {/* Subscription and Payments */}
          <div>
            <h2 className="text-2xl font-semibold text-text-primary mb-4">5. Subscription and Payments</h2>
            <div className="space-y-4 text-text-secondary">
              <h3 className="text-lg font-medium text-text-primary">5.1 Subscription Plans</h3>
              <p>We offer various subscription tiers with different features and pricing. Details of current plans are available on our pricing page.</p>

              <h3 className="text-lg font-medium text-text-primary mt-6">5.2 Billing</h3>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Subscriptions are billed in advance on a recurring basis (monthly or annually)</li>
                <li>Payment is processed through Stripe using the payment method you provide</li>
                <li>You authorize us to charge your payment method for all applicable fees</li>
              </ul>

              <h3 className="text-lg font-medium text-text-primary mt-6">5.3 Cancellation</h3>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>You may cancel your subscription at any time through your account settings</li>
                <li>Cancellation takes effect at the end of your current billing period</li>
                <li>No refunds are provided for partial billing periods</li>
              </ul>

              <h3 className="text-lg font-medium text-text-primary mt-6">5.4 Price Changes</h3>
              <p>We reserve the right to modify pricing. You will be notified at least 30 days before any price increase takes effect.</p>
            </div>
          </div>

          {/* Intellectual Property */}
          <div>
            <h2 className="text-2xl font-semibold text-text-primary mb-4">6. Intellectual Property</h2>
            <div className="space-y-4 text-text-secondary">
              <h3 className="text-lg font-medium text-text-primary">6.1 Our Property</h3>
              <p>The Service, including its design, features, and technology, is owned by Vettis and protected by intellectual property laws. You may not copy, modify, or distribute our proprietary technology.</p>

              <h3 className="text-lg font-medium text-text-primary mt-6">6.2 Generated Content</h3>
              <p>AI-generated content provided to you may be used for personal and internal business purposes. You acknowledge that:</p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Similar content may be generated for other users</li>
                <li>We make no copyright claims on AI-generated content on your behalf</li>
                <li>Third-party content included may be subject to separate licenses</li>
              </ul>

              <h3 className="text-lg font-medium text-text-primary mt-6">6.3 Your Content</h3>
              <p>You retain ownership of content you upload (such as documents for research). By uploading, you grant us a license to process this content to provide the Service.</p>
            </div>
          </div>

          {/* Limitation of Liability */}
          <div>
            <h2 className="text-2xl font-semibold text-text-primary mb-4">7. Limitation of Liability</h2>
            <div className="bg-error/10 border border-error/30 rounded-lg p-6 space-y-4 text-text-secondary">
              <p className="font-semibold uppercase">To the maximum extent permitted by law:</p>
              <ul className="list-disc list-inside ml-4 space-y-2">
                <li>Vettis is provided "AS IS" without warranties of any kind, express or implied</li>
                <li>We do not warrant that the Service will be uninterrupted, error-free, or secure</li>
                <li>We are not liable for any indirect, incidental, special, consequential, or punitive damages</li>
                <li>Our total liability is limited to the amount you paid for the Service in the 12 months preceding the claim</li>
                <li>We are not responsible for any decisions you make based on AI-generated content</li>
              </ul>
            </div>
          </div>

          {/* Indemnification */}
          <div>
            <h2 className="text-2xl font-semibold text-text-primary mb-4">8. Indemnification</h2>
            <div className="space-y-4 text-text-secondary">
              <p>You agree to indemnify and hold harmless Vettis, its officers, directors, employees, and agents from any claims, damages, or expenses arising from:</p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Your use of the Service</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any third-party rights</li>
                <li>Content you upload to the Service</li>
              </ul>
            </div>
          </div>

          {/* Termination */}
          <div>
            <h2 className="text-2xl font-semibold text-text-primary mb-4">9. Termination</h2>
            <div className="space-y-4 text-text-secondary">
              <p>We may suspend or terminate your access to the Service:</p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>If you violate these Terms</li>
                <li>If required by law</li>
                <li>If we discontinue the Service (with reasonable notice)</li>
              </ul>
              <p className="mt-4">Upon termination, your right to use the Service ceases immediately. Provisions that should survive termination (such as limitation of liability) will remain in effect.</p>
            </div>
          </div>

          {/* Governing Law */}
          <div>
            <h2 className="text-2xl font-semibold text-text-primary mb-4">10. Governing Law and Disputes</h2>
            <div className="space-y-4 text-text-secondary">
              <p>These Terms are governed by the laws of the State of Delaware, United States. Any disputes shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association, unless you are entitled to bring claims in small claims court.</p>
              <p className="mt-4">You agree to resolve disputes on an individual basis and waive any right to participate in class actions.</p>
            </div>
          </div>

          {/* Changes to Terms */}
          <div>
            <h2 className="text-2xl font-semibold text-text-primary mb-4">11. Changes to Terms</h2>
            <div className="space-y-4 text-text-secondary">
              <p>We may update these Terms from time to time. Material changes will be notified via email or through the Service at least 30 days before taking effect. Continued use after changes constitutes acceptance of the new Terms.</p>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h2 className="text-2xl font-semibold text-text-primary mb-4">12. Contact Information</h2>
            <div className="space-y-4 text-text-secondary">
              <p>For questions about these Terms, contact us at:</p>
              <div className="bg-surface rounded-lg p-4 border border-border">
                <p><strong>Vettis</strong></p>
                <p>Email: <a href="mailto:contact@vettis.ai" className="text-primary hover:underline">contact@vettis.ai</a></p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </LegalLayout>
    </>
  );
}

